import { FeatureFlag, type InviteUserRequest, Role } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { P, z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { useInviteUserMutation } from '../../graphql/hooks/useInviteUserMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';
import { UserForm } from './UserForm';

type FormValues = InviteUserRequest;

const baseSchema = z.object({
  organizationId: z.string(),
  name: z.string(),
  email: z.email(),
  title: z.string(),
  practitionerNumber: z.string().nullish(),
  picture: z.string().nullish(),
  featureFlags: z.array(z.enum(FeatureFlag)),
});

const rolesSchema = z
  .object({
    roles: z.array(z.enum(Role)).nonempty('You must select at least one role'),
    locations: z.array(z.string()),
  })
  .refine(
    (values) => {
      const isTeamLeaderRole = values.roles?.includes(Role.TeamLeader);
      const hasNoLocations = P.isEmpty(values.locations ?? []);
      return !(isTeamLeaderRole && hasNoLocations);
    },
    {
      message: 'You must select at least one location for a team leader',
      path: ['locations'],
    },
  );

export const userSchema: z.ZodType<FormValues> = z.intersection(baseSchema, rolesSchema);

export const UserInviteModal = createModal((props) => {
  const { modal } = props;
  const { visible, hide, remove } = modal;
  const { organization, successToast, errorToast } = useAppContext();

  const mutation = useInviteUserMutation();

  const methods = useForm<FormValues>({
    defaultValues: {
      organizationId: organization.id,
      name: undefined,
      email: undefined,
      title: undefined,
      practitionerNumber: undefined,
      picture: null,
      roles: [],
      locations: [],
      featureFlags: [],
    },
    resolver: zodResolver(userSchema),
  });

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          request: P.deepTrim({
            organizationId: organization.id,
            name: values.name,
            email: values.email,
            picture: values.picture,
            title: values.title,
            practitionerNumber: values.practitionerNumber,
            roles: values.roles,
            locations: values.locations,
            featureFlags: values.featureFlags,
          }),
        })
        .then(() => {
          hide();
          successToast('User invited successfully');
        })
        .catch((err) => {
          errorToast(`Error inviting user: ${err.message}`);
        });
    },
    [mutation, organization.id, errorToast, hide, successToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Create a user'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <UserForm />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Create</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
